/**
 * Gather all data for the CoCM billing report (the Step 3 clipboard artifact).
 *
 * Composes existing, tested `@benchmark/core` services/resolvers into the
 * serializer-ready `BillingReportData` DTO. The AI clinical narrative is NOT
 * generated here — it is the already-persisted `clinicalSummary` passed in by the
 * caller (which now also carries the psych-consult statement, produced by the
 * summarizer). Goals, referrals, and the standalone psychiatric-consult section
 * were removed from the report; the Connections ID was dropped (the MRN is
 * retained).
 *
 * Also returns `missingFields` so the workflow modal can warn (not silently
 * blank) when Date of Intake or Enrollment Date cannot be resolved.
 *
 * @agentic connections
 */

import type { MedplumClient } from "@medplum/core";
import type {
  Patient,
  Observation,
  Condition,
  CarePlan,
} from "@medplum/fhirtypes";
import {
  getPatientDisplayName,
  getPatientMRN,
  getPatientManagingOrganization,
  getPCPDisplayName,
  getLCSWDisplayName,
  resolveCareTimeActivityDetail,
  formatCareTimeActivityLabel,
  computeAssessmentTrends,
  monthOfEnrollment,
} from "@benchmark/core/resolvers";
import {
  getPatientReferralInfo,
  findEarliestIntakeEncounter,
  searchProblemListConditions,
  searchCareTimeByPatient,
  searchAllFinalSurveyObservations,
  resolveCoCMConsent,
} from "@benchmark/core/services";
import { isCareTimeNonBillable } from "@benchmark/core/constants";
import type {
  BillingReportData,
  BillingReportAssessmentScore,
  BillingReportTimeEntry,
} from "@benchmark/core/types";

const ICD10_CM_SYSTEM = "http://hl7.org/fhir/sid/icd-10-cm";

/** Per-field resolution status for the header dates the UI may warn about. */
export type BillingReportMissingFields = {
  /** `found` = date populated; `absent` = no intake on record; `failed` = read errored. */
  intake: "found" | "absent" | "failed";
  /** `found` = date populated; `absent` = no enrollment on record; `failed` = read errored. */
  enrollment: "found" | "absent" | "failed";
};

/** Gather result: the serializer DTO plus header-field resolution status. */
export type GatheredBillingReport = {
  data: BillingReportData;
  missingFields: BillingReportMissingFields;
};

/** Per-month eligibility figures the caller already computed. */
export type BillingReportEligibility = {
  billableMinutes: number;
  /** Package display, e.g. "99492" or "99492 + 99494×2". */
  billingCode: string;
  /** CoCM CarePlan.period.start (enrollment date). */
  enrollmentDate?: string;
  /** Active CoCM CarePlan id. */
  cocmCarePlanId?: string;
};

const monthBounds = (billingMonth: string): { start: string; end: string } => {
  const [year, month] = billingMonth.split("-").map(Number);
  return {
    start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
    end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString(),
  };
};

const icd10Code = (condition: Condition): string | undefined =>
  condition.code?.coding?.find((c) => c.system === ICD10_CM_SYSTEM)?.code;

const toAssessmentScores = (
  observations: Observation[],
): BillingReportAssessmentScore[] =>
  computeAssessmentTrends(observations).map((trend) => ({
    label: trend.label,
    value: trend.latestValue,
    date: trend.latestDate,
    baselineValue: trend.baselineValue,
    baselineDate: trend.baselineDate,
    delta: trend.delta,
    singleObservation: trend.singleObservation,
  }));

const toTimeEntry = (obs: Observation): BillingReportTimeEntry => {
  const detail = resolveCareTimeActivityDetail(obs);
  return {
    date: obs.effectiveDateTime ?? obs.effectivePeriod?.start,
    activity: formatCareTimeActivityLabel(detail),
    minutes: obs.valueQuantity?.value ?? 0,
    note: detail.note,
    nonBillable: isCareTimeNonBillable(obs),
  };
};

/**
 * Assemble the billing report DTO for a patient + month, plus the header-field
 * resolution status for the Step 3 warning.
 * @agentic connections
 */
export const gatherBillingReportData = async (
  medplum: MedplumClient,
  patient: Patient,
  billingMonth: string,
  eligibility: BillingReportEligibility,
  clinicalSummary: string | undefined,
  options: {
    /**
     * Pre-resolved care-time entries for the month. Callers pass the frozen
     * contributing entries for a closed month (or the live month scan) so the
     * time log matches the surface's own activity log. When omitted, the month
     * is scanned live.
     */
    careTimeEntries?: Observation[];
  } = {},
): Promise<GatheredBillingReport> => {
  const patientId = patient.id ?? "";
  const { start, end } = monthBounds(billingMonth);
  const carePlanId = eligibility.cocmCarePlanId;

  // Required clinical reads: a FAILURE must reject (the caller then aborts the
  // copy) rather than silently rendering an empty section. Surveys are fetched
  // in full (paginated) so the earliest-per-instrument baseline is never
  // truncated by a page cap.
  const [conditions, careTime, surveys] = await Promise.all([
    searchProblemListConditions(medplum, patientId),
    options.careTimeEntries
      ? Promise.resolve(options.careTimeEntries)
      : searchCareTimeByPatient(medplum, patientId, {
          startDate: start,
          endDate: end,
          count: 1000,
        }),
    searchAllFinalSurveyObservations(medplum, patientId, end),
  ]);

  // Best-effort / tolerant reads. Intake and enrollment carry a discriminated
  // status so the UI can distinguish "no date on record" from "the lookup
  // failed" and warn accordingly instead of showing a silent blank.
  const [referralInfo, intakeResult] = await Promise.all([
    getPatientReferralInfo(medplum, patientId).catch(() => undefined),
    findEarliestIntakeEncounter(medplum, patientId),
  ]);

  const carePlanResult: {
    status: "found" | "absent" | "failed";
    carePlan?: CarePlan;
  } = carePlanId
    ? await medplum
        .readResource("CarePlan", carePlanId)
        .then((carePlan) => ({ status: "found" as const, carePlan }))
        .catch(() => ({ status: "failed" as const }))
    : { status: "absent" as const };

  // Reconcile consent from the Patient and the CoCM CarePlan so the report
  // never shows a blank consent date for a patient consented only at enrollment
  // (the CarePlan-only case). Same reconciler as the eligibility service and the
  // "Consented" chip, so all consent surfaces agree.
  const consent = resolveCoCMConsent(patient, carePlanResult.carePlan);
  // Enrollment is the CoCM episode start (CarePlan.period.start), authoritative
  // over the caller-passed estimate.
  const enrollmentDate =
    carePlanResult.carePlan?.period?.start ?? eligibility.enrollmentDate;

  const intakeDate =
    intakeResult.status === "found"
      ? intakeResult.encounter.period?.start
      : undefined;

  // Derive the warning status from the resolved DATE, not just the lookup: a
  // "found" encounter with no period.start still leaves a blank date, so it must
  // warn (as absent) rather than silently render an em-dash.
  const intakeStatus: BillingReportMissingFields["intake"] = intakeDate
    ? "found"
    : intakeResult.status === "failed"
      ? "failed"
      : "absent";

  const enrollmentStatus: BillingReportMissingFields["enrollment"] =
    enrollmentDate
      ? "found"
      : carePlanResult.status === "failed"
        ? "failed"
        : "absent";

  const data: BillingReportData = {
    patientName: getPatientDisplayName(patient),
    mrn: getPatientMRN(patient)?.value,
    dob: patient.birthDate,
    practice: getPatientManagingOrganization(patient)?.name,
    referringProvider: getPCPDisplayName(patient) ?? undefined,
    careManager: getLCSWDisplayName(patient) ?? undefined,
    referralDate: referralInfo?.date,
    intakeDate,
    enrollmentDate,
    consentDate: consent.consentDate,
    billingMonth,
    billingCode: eligibility.billingCode,
    diagnosisCodes: conditions
      .map(icd10Code)
      .filter((code): code is string => Boolean(code)),
    monthOfEnrollment: monthOfEnrollment(enrollmentDate, billingMonth),
    billableMinutes: eligibility.billableMinutes,
    clinicalSummary,
    assessmentScores: toAssessmentScores(surveys),
    timeLog: careTime.map(toTimeEntry),
  };

  return {
    data,
    missingFields: {
      intake: intakeStatus,
      enrollment: enrollmentStatus,
    },
  };
};
